import { useEffect, useRef, useState } from 'react'
import { Pencil, Trash2, Plus, Camera, Upload, Eye } from 'lucide-react'
import api from '../services/api'
import LookupSelect from './LookupSelect'
import type { ChaveOcorrencia } from './OcorrenciaTextoModal'
import { reduzirImagem } from '../utils/reduzirImagem'

// Envolvidos no sinistro (TOCOEVV, frota.ocoenv) — vários por ocorrência; Seguradora = fornecedor do clicad (cli_mat)

interface Envolvido { envnum: number; envnme: string; envtel: string; envend: string; envvei: string; envpla: string; envseg: string; segnome: string; temfoto?: boolean }
const vazio = (): Envolvido => ({ envnum: 0, envnme: '', envtel: '', envend: '', envvei: '', envpla: '', envseg: '', segnome: '' })

const fone = (t: string) => {
  const d = t.replace(/\D/g, '')
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
  return d
}

export default function OcorrenciaEnvolvidosModal({ chave, funcionario, tipo, onClose, somenteLeitura }: {
  chave: ChaveOcorrencia; funcionario: string; tipo: string; onClose: () => void; somenteLeitura?: boolean
}) {
  const [lista, setLista] = useState<Envolvido[] | null>(null)
  const [atual, setAtual] = useState<Envolvido | null>(null)
  const [erro, setErro] = useState('')
  const [aviso, setAviso] = useState('')
  const [salvando, setSalvando] = useState(false)
  const set = <K extends keyof Envolvido>(k: K, v: Envolvido[K]) => setAtual(x => (x ? { ...x, [k]: v } : x))

  async function carregar() {
    const r = await api.get('/ocorrencias-funcionarios/envolvidos', { params: chave })
    setLista(r.data)
    return r.data as Envolvido[]
  }
  useEffect(() => {
    carregar().then(l => { if (!l.length) abrirForm(vazio()) }).catch(() => setErro('Não foi possível carregar os envolvidos.'))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Foto do veículo (frota.ocoenv_foto): envolvido já gravado sobe na hora; novo fica pendente até incluir
  const [foto, setFoto] = useState<string | null>(null)
  const [fotoPendente, setFotoPendente] = useState<string | null>(null)
  const [enviandoFoto, setEnviandoFoto] = useState(false)
  const arquivoRef = useRef<HTMLInputElement | null>(null)

  function abrirForm(e: Envolvido) {
    setAtual({ ...e }); setErro(''); setAviso('')
    setFoto(null); setFotoPendente(null)
    if (e.envnum && e.temfoto) {
      api.get('/ocorrencias-funcionarios/envolvidos/foto', { params: { ...chave, envnum: e.envnum }, responseType: 'blob' })
        .then(r => setFoto(URL.createObjectURL(r.data))).catch(() => {})
    }
  }

  async function escolherFoto(arquivo: File) {
    if (!atual) return
    setErro(''); setEnviandoFoto(true)
    try {
      const imagem = await reduzirImagem(arquivo)
      if (atual.envnum) {
        await api.put('/ocorrencias-funcionarios/envolvidos/foto', { ...chave, envnum: atual.envnum, imagem })
        await carregar()
      } else setFotoPendente(imagem)
      setFoto(imagem)
    } catch (e: any) {
      setErro(e?.response?.data?.error || e?.message || 'Erro ao enviar a foto.')
    } finally {
      setEnviandoFoto(false)
    }
  }

  async function removerFoto() {
    if (!atual) return
    if (atual.envnum) {
      if (!window.confirm('Remover a foto do veículo deste envolvido?')) return
      try { await api.delete('/ocorrencias-funcionarios/envolvidos/foto', { params: { ...chave, envnum: atual.envnum } }); await carregar() }
      catch (e: any) { setErro(e?.response?.data?.error || 'Erro ao remover a foto.'); return }
    }
    setFoto(null); setFotoPendente(null)
  }

  async function salvar() {
    if (!atual) return
    if (!atual.envnme.trim()) { setErro('Informe o nome do envolvido.'); return }
    setErro(''); setAviso('')
    setSalvando(true)
    let novo = 0
    try {
      if (atual.envnum) await api.put('/ocorrencias-funcionarios/envolvidos', { ...chave, ...atual })
      else {
        novo = (await api.post('/ocorrencias-funcionarios/envolvidos', { ...chave, ...atual })).data.envnum
        if (fotoPendente) await api.put('/ocorrencias-funcionarios/envolvidos/foto', { ...chave, envnum: novo, imagem: fotoPendente })
      }
      await carregar()
      setAviso(atual.envnum ? `Envolvido ${atual.envnum} salvo.` : `Envolvido ${novo} incluído.`)
      setAtual(null); setFoto(null); setFotoPendente(null)
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Erro ao salvar o envolvido.'
      setErro(novo ? `Envolvido ${novo} incluído, mas a foto não foi enviada: ${msg}` : msg)
      if (novo) { await carregar().catch(() => {}); setAtual(null) }
    } finally {
      setSalvando(false)
    }
  }

  async function excluir(e: Envolvido) {
    if (!window.confirm(`Excluir o envolvido ${e.envnum} - ${e.envnme}?`)) return
    setErro(''); setAviso('')
    try {
      await api.delete('/ocorrencias-funcionarios/envolvidos', { params: { ...chave, envnum: e.envnum } })
      await carregar()
      if (atual?.envnum === e.envnum) setAtual(null)
    } catch (err: any) {
      setErro(err?.response?.data?.error || 'Erro ao excluir.')
    }
  }

  const info = (rot: string, val: string | number) => (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{rot}</div>
      <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1b3f7b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={String(val)}>{val}</div>
    </div>
  )
  const th: React.CSSProperties = { padding: '0.45rem 0.6rem', fontSize: '0.68rem', fontWeight: 800, color: '#fff', background: '#1b3f7b', textAlign: 'left', whiteSpace: 'nowrap', textTransform: 'uppercase' }
  const td: React.CSSProperties = { padding: '0.4rem 0.6rem', fontSize: '0.82rem', borderBottom: '1px solid #eef2f7', whiteSpace: 'nowrap' }
  const cheio: React.CSSProperties = { width: '100%', boxSizing: 'border-box' }

  return (
    <div className="modal-overlay" style={{ zIndex: 1300 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 900, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)', gap: '1rem' }}>
          <div style={{ minWidth: 0 }}><h2>Sinistro — Envolvidos</h2></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0, marginLeft: 'auto' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'rgba(219,234,254,0.8)' }}>TOCOEVV</span>
            <button className="modal-fechar" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="modal-corpo" style={{ background: '#f1f5fb', overflowY: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '110px minmax(0, 1fr) minmax(0, 1fr) 90px', gap: '0.75rem', padding: '0.75rem 1rem',
            background: '#fff', border: '1px solid #c7d6ea', borderRadius: 10, marginBottom: '0.9rem' }}>
            {info('Data', chave.ocodat.split('-').reverse().join('/'))}
            {info('Funcionário', funcionario || '—')}
            {info('Tipo', tipo)}
            {info('Número', chave.oconro)}
          </div>

          {lista === null ? (
            <p style={{ color: erro ? '#dc2626' : '#6b7280' }}>{erro || 'Carregando...'}</p>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#1b3f7b' }}>Envolvidos ({lista.length})</span>
                {!somenteLeitura && <button className="btn btn-primary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                  onClick={() => abrirForm(vazio())}>
                  <Plus size={14} /> Novo envolvido
                </button>}
              </div>
              {lista.length > 0 && (
                <div style={{ overflowX: 'auto', border: '1px solid #bfdbfe', borderRadius: 8, background: '#fff', marginBottom: '0.9rem' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ ...th, width: 76 }}></th><th style={th}>Nº</th><th style={th}>Nome</th><th style={th}>Fone</th>
                        <th style={th}>Veículo</th><th style={th}>Placa</th><th style={th}>Seguradora</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lista.map((e, i) => (
                        <tr key={e.envnum} style={{ background: atual?.envnum === e.envnum ? '#dbeafe' : i % 2 ? '#f5f9fd' : '#fff' }}>
                          <td style={{ ...td, display: 'flex', gap: 4 }}>
                            <button className="row-action-btn row-action-btn--edit" title={somenteLeitura ? "Ver detalhes" : "Editar"} onClick={() => abrirForm(e)}>{somenteLeitura ? <Eye size={14} /> : <Pencil size={14} />}</button>
                            {!somenteLeitura && <button className="row-action-btn" title="Excluir" style={{ color: '#b91c1c' }} onClick={() => excluir(e)}><Trash2 size={14} /></button>}
                          </td>
                          <td style={td}>{e.envnum}</td>
                          <td style={{ ...td, fontWeight: 600 }}>{e.envnme}</td>
                          <td style={td}>{fone(e.envtel)}</td>
                          <td style={td}>{e.envvei}</td>
                          <td style={td}>{e.envpla}</td>
                          <td style={{ ...td, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }} title={e.segnome}>{e.segnome || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {atual && (
                <div style={{ border: '1px solid #c7d6ea', borderRadius: 10, background: '#fff', padding: '0.9rem 1rem' }}>
                  <div style={{ fontSize: '0.74rem', fontWeight: 800, color: '#1b3f7b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.7rem' }}>
                    {atual.envnum ? `Envolvido nº ${atual.envnum}` : 'Novo envolvido (número gerado ao salvar)'}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 200px', gap: '1rem', alignItems: 'start' }}>
                  <fieldset disabled={somenteLeitura} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 170px', gap: '0.75rem 1rem' }}>
                    <div className="field" style={{ margin: 0 }}>
                      <label>Nome do Envolvido *</label>
                      <input value={atual.envnme} maxLength={50} style={cheio} autoFocus onChange={e => set('envnme', e.target.value)} />
                    </div>
                    <div className="field" style={{ margin: 0 }}>
                      <label>Fone</label>
                      <input value={atual.envtel} maxLength={10} inputMode="numeric" style={cheio} placeholder="DDD + número"
                        onChange={e => set('envtel', e.target.value.replace(/\D/g, ''))} />
                    </div>
                    <div className="field" style={{ margin: 0, gridColumn: '1 / 3' }}>
                      <label>Endereço</label>
                      <input value={atual.envend} maxLength={50} style={cheio} onChange={e => set('envend', e.target.value)} />
                    </div>
                    <div className="field" style={{ margin: 0 }}>
                      <label>Veículo</label>
                      <input value={atual.envvei} maxLength={30} style={cheio} placeholder="Marca/modelo/cor" onChange={e => set('envvei', e.target.value)} />
                    </div>
                    <div className="field" style={{ margin: 0 }}>
                      <label>Placa</label>
                      <input value={atual.envpla} maxLength={8} style={{ ...cheio, textTransform: 'uppercase' }} onChange={e => set('envpla', e.target.value.toUpperCase())} />
                    </div>
                    <div className="field" style={{ margin: 0, gridColumn: '1 / 3' }}>
                      <label>Seguradora</label>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <LookupSelect endpoint="/erp/clientes/lista" codKey="cli_mat" descKey="cli_nom" tagKey="cli_mat" somenteComTexto
                            codValue={atual.envseg} descValue={atual.segnome} listWidth={460}
                            onSelect={(c, d) => setAtual(x => (x ? { ...x, envseg: c, segnome: d } : x))} placeholder="Buscar seguradora por nome ou CNPJ..." disabled={somenteLeitura} />
                        </div>
                        {atual.envseg && <button className="btn btn-outline btn-sm" title="Limpar" onClick={() => setAtual(x => (x ? { ...x, envseg: '', segnome: '' } : x))}>✕</button>}
                      </div>
                    </div>
                  </div>
                  </fieldset>

                  <div>
                    <span style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.35rem' }}>
                      <Camera size={13} style={{ verticalAlign: -2, marginRight: 4 }} />Foto do Veículo
                    </span>
                    <input ref={arquivoRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }}
                      onChange={e => { const a = e.target.files?.[0]; e.target.value = ''; if (a) escolherFoto(a) }} />
                    {foto
                      ? <a href={foto} target="_blank" rel="noreferrer" title="Abrir em tamanho real"
                          style={{ display: 'block', height: 150, borderRadius: 10, border: '1px solid #c7d6ea', overflow: 'hidden', background: '#fff' }}>
                          <img src={foto} alt="Foto do veículo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        </a>
                      : somenteLeitura
                        ? <div style={{ height: 150, borderRadius: 10, border: '1px solid #c7d6ea', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '0.78rem' }}>Sem foto</div>
                      : <button type="button" onClick={() => arquivoRef.current?.click()} disabled={enviandoFoto}
                          style={{ width: '100%', height: 150, borderRadius: 10, border: '1.5px dashed #9fbbe0', background: '#f5f9fe', cursor: 'pointer',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, color: '#1a6ea8', fontSize: '0.78rem', fontWeight: 600 }}>
                          <Camera size={24} />
                          {enviandoFoto ? 'Enviando...' : 'Clique para adicionar'}
                        </button>}
                    {!somenteLeitura && <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                      <button type="button" className="btn btn-primary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                        onClick={() => arquivoRef.current?.click()} disabled={enviandoFoto}>
                        <Upload size={14} /> {foto ? 'Trocar' : 'Adicionar'}
                      </button>
                      {foto && (
                        <button type="button" className="btn btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#fff', color: '#b91c1c', border: '1px solid #fecaca' }}
                          onClick={removerFoto}>
                          <Trash2 size={14} /> Remover
                        </button>
                      )}
                    </div>}
                    {!atual.envnum && fotoPendente && <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: 4 }}>enviada ao incluir</div>}
                  </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: '0.9rem', justifyContent: 'flex-end' }}>
                    <button className="btn btn-outline" onClick={() => setAtual(null)}>{somenteLeitura ? 'Fechar detalhe' : 'Cancelar'}</button>
                    {!somenteLeitura && <button className="btn btn-primary" onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : atual.envnum ? 'Salvar envolvido' : 'Incluir envolvido'}</button>}
                  </div>
                </div>
              )}
            </>
          )}

          {lista !== null && erro && <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '0.8rem 0 0' }}>{erro}</p>}
          {aviso && <p style={{ color: '#16a34a', fontSize: '0.82rem', margin: '0.8rem 0 0' }}>{aviso}</p>}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  )
}
